import { z } from "zod/v4";
import { defineEndpoint, defineResource } from "@visioni/a3s-client-api";
import { stringToInt } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../feature-name";
import { ActivationOperatorNote, FleetClientNoParam, ListOrder, MatchedClientNos } from "./activations";

/**
 * Patriot operator notes across the fleet — what monitoring operators and
 * Patriot's own automation wrote against each client, activation or not.
 *
 * Notes without a ticket are the reason this list exists: autodial call logs,
 * 000 calls among them, sit on a client with no activation, so no activation
 * view shows them.
 *
 * Support-only, like every `patriot/fleet/` route. A note row is the
 * activation detail's {@link ActivationOperatorNote}, so the same caveats hold —
 * `notes` is unreviewed operator free text — plus the Patriot client number,
 * which the activation shapes leave out as a mapping detail but which is how
 * support finds a client here.
 */

export const FleetOperatorNoteSummary = ActivationOperatorNote.extend({
  /** Patriot's client number, e.g. `10539BASE01`. */
  clientNo: z.string(),
  /** The activation the note was written on. Absent on a ticketless note. */
  ticketNo: z.string().optional(),
});
export type FleetOperatorNoteSummary = z.output<typeof FleetOperatorNoteSummary>;

export const ListFleetOperatorNotesResponse = z.object({
  items: z.array(FleetOperatorNoteSummary),
  /**
   * Opaque resume token; `null` when the window is exhausted. Pass back
   * verbatim. Unfiltered, a page can come back empty with a non-null cursor,
   * as the fleet activations can — page until this is `null`.
   */
  cursor: z.string().nullable(),
  clientNos: MatchedClientNos,
});
export type ListFleetOperatorNotesResponse = z.output<typeof ListFleetOperatorNotesResponse>;

/**
 * A time window as the fleet activations take one — `since` required, for the
 * same reason — plus the start of a client number, as they take it (see
 * {@link FleetClientNoParam}).
 */
export const ListFleetOperatorNotesQueryParams = z
  .object({
    limit: stringToInt.pipe(z.int().positive().max(100)).default(20),
    cursor: z.string().optional(),
    since: z.iso.datetime({ offset: true }),
    until: z.iso.datetime({ offset: true }).optional(),
    order: ListOrder.optional(),
    clientNo: FleetClientNoParam,
  })
  .refine((q) => q.until === undefined || Date.parse(q.since) <= Date.parse(q.until), {
    message: "until cannot be before since",
    path: ["until"],
  });
export type ListFleetOperatorNotesQueryParamsWire = z.input<typeof ListFleetOperatorNotesQueryParams>;
export type ListFleetOperatorNotesQueryParams = z.output<typeof ListFleetOperatorNotesQueryParams>;

/** Under the caller's tenant in the path only because every client-API route is; see `fleetActivationsResource`. */
export const fleetOperatorNotesResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/operator-notes",
  scope: "tenant",
});

export const listFleetOperatorNotesEndpoint = defineEndpoint(fleetOperatorNotesResource, {
  responseSchema: ListFleetOperatorNotesResponse,
  querySchema: ListFleetOperatorNotesQueryParams,
});
